import {
    AudienceId,
    EmailMarketingProvider,
    formatDateNoTime,
    parseEntityIdSafe,
    TenantCredentialId,
} from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { useHipaaAcknowledgement } from "components/manage/manage-integrations/use-hipaa-acknowledgement";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { InlineBanner, MessageBanner } from "components/ui/inline-banner";
import { useMessage } from "hooks/use-message";
import { PrimaryButton, primaryClasses } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    GetAudiencesQuery,
    GetTenantCredentialsQuery,
    TenantCredentialStatus,
    TenantCredentialType,
    useCreateAudienceMutation,
    useDeleteTenantCredentialMutation,
    useGetAudiencesQuery,
    useGetTenantCredentialsQuery,
} from "generated/graphql";
import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { AudienceLink } from "./audience-link";
import { AudienceShow } from "./audience-show";

export interface AudienceManagerConfig {
    providerName: string;
    credentialType: TenantCredentialType;
    redirectPath: string;
    authUri: string;
    provider: EmailMarketingProvider;
}

interface AudienceManagerProps {
    config: AudienceManagerConfig;
    match?: {
        path?: string;
        params?: {
            audienceId?: string;
            subtab?: string;
            audienceTab?: string;
        };
    };
}

export const AudienceManager: React.FC<AudienceManagerProps> = props => {
    const { config, match } = props;
    const regionId = useRegionId();
    const location = useLocation();
    const history = useHistory();
    const { message, setMessage } = useMessage(8000);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const authStatus = params.get("authstatus");
        if (authStatus === "success") {
            setMessage("success", `${config.providerName} connected successfully.`);
        } else if (authStatus === "denied") {
            setMessage(
                "warning",
                `${config.providerName} authorization was cancelled. No changes were made.`,
            );
        }
        if (authStatus) {
            history.replace(location.pathname);
        }
    }, [config.providerName, history, location.pathname, location.search, setMessage]);

    const createAudienceMutation = useCreateAudienceMutation();
    const credentialsQuery = useGetTenantCredentialsQuery();

    const onNewAudience = React.useCallback(
        async (e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e?.preventDefault();

            const audience = await createAudienceMutation.mutateAsync({
                input: {
                    name: "New Contact List",
                    regionId,
                },
            });

            navigate(
                `${config.redirectPath}/audiences/${audience.createAudience.id}?initialMode=edit`,
            );
        },
        [createAudienceMutation, regionId, config.redirectPath],
    );

    const audienceId = match?.params?.audienceId
        ? parseEntityIdSafe<AudienceId>(match.params.audienceId).getOrElse(null)
        : null;

    const subtab = match?.params?.subtab ? match.params.subtab : null;
    const audienceTab = match?.params?.audienceTab ? match.params.audienceTab : null;

    return (
        <>
            <MessageBanner message={message} />
            {audienceId ? (
                <AudienceShow audienceId={audienceId} subtab={audienceTab} />
            ) : null}
            {subtab ? (
                <ContentFull>
                    <ContentHeader>{config.providerName}</ContentHeader>
                    <QueryRenderer query={credentialsQuery} name={config.providerName}>
                        {data => {
                            const credential = data.getTenantCredentials.find(
                                cred => cred.type === config.credentialType,
                            );

                            return (
                                <>
                                    <ProviderIntegration
                                        config={config}
                                        credential={credential}
                                        refetchCredentials={credentialsQuery.refetch}
                                    />
                                    {subtab === "audiences" ? (
                                        <AudiencesSection
                                            onCreate={onNewAudience}
                                            isConnected={
                                                credential?.status ===
                                                TenantCredentialStatus.Active
                                            }
                                            config={config}
                                        />
                                    ) : null}
                                </>
                            );
                        }}
                    </QueryRenderer>
                </ContentFull>
            ) : null}
        </>
    );
};

interface AudiencesSectionProps {
    onCreate: () => void;
    isConnected: boolean;
    config: AudienceManagerConfig;
}

const AudiencesSection: React.FC<AudiencesSectionProps> = props => {
    const { onCreate, isConnected, config } = props;

    if (!isConnected) {
        return null;
    }

    return <AudiencesList onCreate={onCreate} config={config} />;
};

interface AudiencesListProps {
    onCreate: () => void;
    config: AudienceManagerConfig;
}

const AudiencesList: React.FC<AudiencesListProps> = props => {
    const { onCreate, config } = props;
    const regionId = useRegionId();
    const audiencesQuery = useGetAudiencesQuery({
        input: {
            regionId,
        },
    });
    const audiences = audiencesQuery.data?.getAudiences ?? [];

    const data = audiences;

    const handleCreate = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            onCreate();
        },
        [onCreate],
    );

    const columns: CrmTableProps<(typeof data)[0]>["columns"] = [
        {
            key: "name",
            header: "Name",
            renderCell: ({
                record,
            }: {
                record: GetAudiencesQuery["getAudiences"][0];
            }) => {
                return <AudienceLink audience={record} provider={config.provider} />;
            },
        },
        {
            key: "updatedAt",
            header: "Last updated",
            renderCell: ({
                record,
            }: {
                record: GetAudiencesQuery["getAudiences"][0];
            }) => {
                return <span>{formatDateNoTime(record.updatedAt)}</span>;
            },
        },
    ];

    if (data.length === 0) {
        return (
            <div>
                <div className="p-2 space-y-4">
                    <p>
                        Create a contact list from criteria you define across clients,
                        communities, and referral contacts.
                    </p>
                    <PrimaryButton onClick={handleCreate}>
                        Create Contact List
                    </PrimaryButton>
                </div>
            </div>
        );
    }

    return (
        <div>
            <CrmTable
                data={data}
                columns={columns}
                initialSort={[{ id: "name", desc: false }]}
            />
            <div className="mt-4">
                <PrimaryButton onClick={handleCreate}>Add new contact list</PrimaryButton>
            </div>
        </div>
    );
};

interface ProviderIntegrationProps {
    config: AudienceManagerConfig;
    credential?: GetTenantCredentialsQuery["getTenantCredentials"][0];
    refetchCredentials: () => void;
}

const ProviderIntegration: React.FC<ProviderIntegrationProps> = props => {
    const { config, credential, refetchCredentials } = props;
    const deleteTenantCredentialMutation = useDeleteTenantCredentialMutation();
    const { confirmHipaaRisk } = useHipaaAcknowledgement();

    const handleConnect = React.useCallback(async () => {
        const confirmed = await confirmHipaaRisk(config.providerName);
        if (confirmed) {
            window.location.href = config.authUri;
        }
    }, [confirmHipaaRisk, config.providerName, config.authUri]);

    const handleDisconnect = React.useCallback(
        async (credentialId: TenantCredentialId) => {
            const confirmed = window.confirm(
                `Are you sure you want to disconnect ${config.providerName}? This will remove access to your ${config.providerName} account and stop all syncing.`,
            );

            if (confirmed) {
                await deleteTenantCredentialMutation.mutateAsync({
                    id: credentialId,
                });
                refetchCredentials();
            }
        },
        [deleteTenantCredentialMutation, refetchCredentials, config.providerName],
    );

    return (
        <div className="mb-6">
            {credential ? (
                <div className="space-y-2">
                    {credential.status === TenantCredentialStatus.Active ? (
                        <>
                            <InlineBanner type="success">
                                {config.providerName} is connected. You can now manage
                                contact lists and sync them with {config.providerName}.
                            </InlineBanner>
                            <div>
                                <SecondaryButton
                                    onClick={() => handleDisconnect(credential.id)}
                                    disabled={deleteTenantCredentialMutation.isLoading}>
                                    {deleteTenantCredentialMutation.isLoading
                                        ? "Disconnecting..."
                                        : `Disconnect ${config.providerName}`}
                                </SecondaryButton>
                            </div>
                        </>
                    ) : (
                        <>
                            <InlineBanner type="error">
                                {config.providerName} is no longer connected. Please click{" "}
                                <strong>Reconnect {config.providerName}</strong> below to
                                reconnect.
                            </InlineBanner>
                            <div>
                                <button
                                    onClick={handleConnect}
                                    className={primaryClasses}>
                                    Reconnect {config.providerName}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        Connect your {config.providerName} account to sync contact lists
                        and manage your email marketing campaigns.
                    </div>
                    <div>
                        <button onClick={handleConnect} className={primaryClasses}>
                            Connect to {config.providerName}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
